/**
 * The computation context an indicator's `setup` receives, and the bar views it reads.
 *
 * Execution model (Pine-like, docs/TRADINGVIEW-REFERENCE.md §10): `setup(ctx, params)` runs once
 * per (instance, params, bar series) and wires **nodes** — `ctx.compute`, `ta.*`, `ctx.security` —
 * then returns the per-bar `calc(i)`. For every bar index the runtime runs the nodes in creation
 * order and then `calc(i)`. Nodes and `calc` must be pure functions of the bar data at `≤ i` and of
 * series values at `< i` (their own) — never closure variables that carry state across bars —
 * so re-running a bar is idempotent and incremental == full recompute (tested for every built-in;
 * use `checkIncremental` from `testing.ts` for new indicators).
 *
 * Numeric policy (ADR-0018): bar prices are **absolute integer ticks** (exact in float64 up to
 * 2^53), volumes are integer contracts. Sums/differences of those stay exact; divisions, EMAs and
 * square roots are float64. Price-unit outputs (`format: "price"`) are float ticks: display-only.
 * **Indicator values must never be fed back into order prices** — an order price is always an
 * integer tick chosen by the user or rounded explicitly by the order ticket (SPEC §9).
 */
import type { BarSeries, BarSpec } from "../bars/barBuilder.ts";
import { sessionKey, sessionOpenMs } from "../bars/barBuilder.ts";
import { TradingDayCache, centralOffsetMs } from "../bars/calendar.ts";
import { RTH_CLOSE_MINUTE, RTH_OPEN_MINUTE } from "../bars/levels.ts";
import { parseTimeframe } from "../bars/timeframe.ts";
import { Buf, PlotBuf, type Series } from "./series.ts";
import type { IndicatorExtras } from "./extras.ts";
import type { SymbolFeeds } from "./feeds.ts";
import type { BarSource, Drawing, DrawView, PlotDisplay, SessionAnchor, ValueFormat } from "./types.ts";

/** A `ctx.draw` callback: Pine line/label/box objects from the computed series (see `draw`). */
export type DrawFn = (view: DrawView) => Drawing[];

/** Display-side outputs of a computation besides plots and levels (offsets, drawings). */
export type ContextOutputs = {
  offsets: Map<string, number>;
  draws: DrawFn[];
  /** Input-dependent plot display (`ctx.display`). */
  displays?: Map<string, PlotDisplay>;
  /** Input-dependent `show_last` counts of plots / fills (`ctx.showLast`, `ctx.fillShowLast`). */
  showLasts?: Map<string, number>;
  fillShowLasts?: Map<string, number>;
};

/** OHLCV + time of the bars an indicator runs on (chart bars, or higher-timeframe bars). */
export interface BarsView {
  readonly open: Series;
  readonly high: Series;
  readonly low: Series;
  readonly close: Series;
  readonly volume: Series;
  /** Buy-aggressor volume (ask hits). */
  readonly buyVolume: Series;
  /** Sell-aggressor volume (bid hits). */
  readonly sellVolume: Series;
  /**
   * Highest / lowest running aggressor delta (buy − sell from 0 at the bar open) inside the bar,
   * NaN when the bar does not carry it (exchange history bars; `BarRecord.deltaHigh`).
   */
  readonly deltaHigh: Series;
  readonly deltaLow: Series;
  /** Bar open time, epoch ms. */
  readonly time: Series;
}

/** Read a chart `BarSeries` as absolute ticks. Always reads the current typed arrays (they grow). */
export function barsOf(s: BarSeries): BarsView {
  // One literal per column (no `s[col]` keyed loads: those go megamorphic in the hot loop).
  const ok = (i: number): boolean => i >= 0 && i < s.length;
  const o = (): number => s.origin ?? 0;
  return {
    open: { at: (i) => (ok(i) ? (s.open[i] ?? 0) + o() : Number.NaN) },
    high: { at: (i) => (ok(i) ? (s.high[i] ?? 0) + o() : Number.NaN) },
    low: { at: (i) => (ok(i) ? (s.low[i] ?? 0) + o() : Number.NaN) },
    close: { at: (i) => (ok(i) ? (s.close[i] ?? 0) + o() : Number.NaN) },
    volume: { at: (i) => (ok(i) ? (s.volume[i] ?? Number.NaN) : Number.NaN) },
    buyVolume: { at: (i) => (ok(i) ? (s.buyVolume[i] ?? Number.NaN) : Number.NaN) },
    sellVolume: { at: (i) => (ok(i) ? (s.sellVolume[i] ?? Number.NaN) : Number.NaN) },
    deltaHigh: { at: (i) => (ok(i) ? (s.deltaHigh[i] ?? Number.NaN) : Number.NaN) },
    deltaLow: { at: (i) => (ok(i) ? (s.deltaLow[i] ?? Number.NaN) : Number.NaN) },
    time: { at: (i) => (ok(i) ? (s.openTs[i] ?? Number.NaN) : Number.NaN) },
  };
}

/** A built-in source over a bar view (hl2 = (h+l)/2 …, TradingView's source list). */
export function barSource(b: BarsView, src: BarSource): Series {
  switch (src) {
    case "open":
      return b.open;
    case "high":
      return b.high;
    case "low":
      return b.low;
    case "close":
      return b.close;
    case "volume":
      return b.volume;
    case "hl2":
      return { at: (i) => (b.high.at(i) + b.low.at(i)) / 2 };
    case "hlc3":
      return { at: (i) => (b.high.at(i) + b.low.at(i) + b.close.at(i)) / 3 };
    case "ohlc4":
      return { at: (i) => (b.open.at(i) + b.high.at(i) + b.low.at(i) + b.close.at(i)) / 4 };
    case "hlcc4":
      return { at: (i) => (b.high.at(i) + b.low.at(i) + 2 * b.close.at(i)) / 4 };
  }
}

/** Chained source: `ind:<instanceId>:<plotId>`. */
export function sourceRef(instanceId: string, plotId: string): string {
  return `ind:${instanceId}:${plotId}`;
}

export function parseSourceRef(ref: string): { instanceId: string; plotId: string } | null {
  if (!ref.startsWith("ind:")) return null;
  const rest = ref.slice(4);
  const k = rest.lastIndexOf(":");
  if (k <= 0 || k === rest.length - 1) return null;
  return { instanceId: rest.slice(0, k), plotId: rest.slice(k + 1) };
}

/** What the runtime provides besides the bars. */
export type ContextEnv = {
  spec: BarSpec;
  /** Price of one tick (tickNum / tickDen); NaN until the instrument is known. */
  tickSize: number;
  /** Resolve a chained source (`ind:…`) to the upstream plot; null = unknown. */
  resolve: (ref: string) => Series | null;
  /** Initial series capacity (the bar storage's), so buffers do not grow by doubling. */
  capacity?: number;
  /** Value format of a chained source's upstream indicator (price plots are in ticks). */
  formatOf?: (ref: string) => ValueFormat | null;
  /** The chart's contract ("NQZ6"), for Pine `syminfo.ticker` / `root`; absent = unknown. */
  symbol?: string;
  /** Other symbols / bar specs (Pine `request.security` of another symbol); absent = none here. */
  feeds?: SymbolFeeds;
};

/** Approximate duration of a bar spec (ms) for "is this a higher timeframe" checks. */
export function specDurationMs(spec: BarSpec): number | null {
  if (spec.kind === "time") return spec.periodMs;
  if (spec.kind === "session") {
    const day = 86_400_000;
    return spec.unit === "day"
      ? spec.count * day
      : spec.unit === "week"
        ? spec.count * 7 * day
        : spec.count * 30 * day;
  }
  return null;
}

/** RTH (08:30–15:00 CT) minute window, SPEC §12.4 session anchors (one definition: levels.ts). */
const RTH_OPEN_MIN = RTH_OPEN_MINUTE;
const RTH_CLOSE_MIN = RTH_CLOSE_MINUTE;

/** Minute of the CT day of an instant. */
export function centralMinute(utcMs: number): number {
  const local = utcMs + centralOffsetMs(utcMs);
  const m = Math.floor(local / 60_000) % 1440;
  return m < 0 ? m + 1440 : m;
}

export class Context {
  /** Wired per-bar nodes, run in creation order before `calc`. */
  readonly nodes: ((i: number) => void)[] = [];
  /** Everything that must grow with the bar count. */
  private readonly growables: { ensure(n: number): void }[] = [];
  readonly bars: BarsView;
  readonly env: ContextEnv;
  /** Plot outputs (null in a higher-timeframe sub-context: plots belong to the chart). */
  private readonly plotBufs: Map<string, PlotBuf> | null;
  private readonly levelVals: Map<string, number> | null;
  /** Non-fatal notes shown next to the legend ("MTF approximated on tick bars"). */
  readonly warnings: string[] = [];
  /** Optional visuals beyond plots and drawings (Pine `barcolor`, `plotcandle`; extras.ts). */
  extras: IndicatorExtras | null = null;
  private days: Series | null = null;
  private sessions = new Map<SessionAnchor, Series>();

  private readonly outputs: ContextOutputs | null;

  constructor(
    bars: BarsView,
    env: ContextEnv,
    plots: Map<string, PlotBuf> | null,
    levels: Map<string, number> | null,
    outputs: ContextOutputs | null = null,
  ) {
    this.bars = bars;
    this.env = env;
    this.plotBufs = plots;
    this.levelVals = levels;
    this.outputs = outputs;
    if (plots) for (const p of plots.values()) this.growables.push(p);
  }

  /**
   * Pine `offset` for a plot whose shift depends on inputs (Ichimoku displacement): the value
   * computed at bar `i` is drawn at bar `i + n`. Display only; overrides the plot's `offset`.
   */
  offset(id: string, n: number): void {
    if (!this.outputs) throw new Error("offsets are not available in security()");
    this.outputs.offsets.set(id, Math.trunc(n));
  }

  /**
   * Pine `show_last` for a plot whose count depends on inputs: only the values of the last `n`
   * computed bars are drawn (Infinity / NaN = every bar). Display only; overrides the plot's
   * `showLast`.
   */
  showLast(id: string, n: number): void {
    if (!this.outputs?.showLasts) throw new Error("show_last is not available in security()");
    this.outputs.showLasts.set(id, Number.isFinite(n) ? Math.trunc(n) : Infinity);
  }

  /** `ctx.showLast` for a fill (`FillSpec.showLast`). */
  fillShowLast(id: string, n: number): void {
    if (!this.outputs?.fillShowLasts) throw new Error("show_last is not available in security()");
    this.outputs.fillShowLasts.set(id, Number.isFinite(n) ? Math.trunc(n) : Infinity);
  }

  /**
   * How plot `id` is displayed for these inputs (columns side by side or stacked, or off). Display
   * only: the plot's values are unchanged.
   */
  display(id: string, d: PlotDisplay): void {
    if (!this.outputs?.displays) throw new Error("plot display is not available in security()");
    this.outputs.displays.set(id, d);
  }

  /**
   * Pine drawing objects (line/label/box). `fn` is called at draw time (not per bar) with the
   * visible bar window and the last computed bar; it must be a pure function of the computed
   * series (read them at `≤ view.last`), so the drawings of an incremental and a full computation
   * are identical. Like Pine's `line.new` objects, drawings may change after a bar closed (the
   * last Zig Zag leg moves until the next pivot is confirmed — TradingView does the same).
   */
  draw(fn: DrawFn): void {
    if (!this.outputs) throw new Error("drawings are not available in security()");
    this.outputs.draws.push(fn);
  }

  get open(): Series {
    return this.bars.open;
  }
  get high(): Series {
    return this.bars.high;
  }
  get low(): Series {
    return this.bars.low;
  }
  get close(): Series {
    return this.bars.close;
  }
  get volume(): Series {
    return this.bars.volume;
  }
  get time(): Series {
    return this.bars.time;
  }
  get spec(): BarSpec {
    return this.env.spec;
  }
  get tickSize(): number {
    return this.env.tickSize;
  }

  /** A source input's series (built-in or another indicator's plot). Unknown refs → close. */
  source(ref: string): Series {
    if (ref.startsWith("ind:")) {
      const s = this.env.resolve(ref);
      if (s) return s;
      this.warnings.push("source unavailable — using close");
      return this.bars.close;
    }
    const known = ["open", "high", "low", "close", "hl2", "hlc3", "ohlc4", "hlcc4", "volume"];
    return barSource(this.bars, (known.includes(ref) ? ref : "close") as BarSource);
  }

  /** A state series the caller writes (`calc`/node at `i` may read its own values at `< i` only). */
  series(): Buf {
    const b = new Buf(this.env.capacity);
    this.growables.push(b);
    return b;
  }

  /** A derived series: `fn(i)` is evaluated for each bar, before anything created after it. */
  compute(fn: (i: number) => number): Series {
    const b = this.series();
    this.nodes.push((i) => {
      b.data[i] = fn(i);
    });
    return b;
  }

  /** Low-level: run `fn(i)` per bar at this point of the node order (for `ta` authors). */
  node(fn: (i: number) => void): void {
    this.nodes.push(fn);
  }

  /** The output buffer of plot `id` (declared in the definition's `plots`). */
  plot(id: string): PlotBuf {
    const p = this.plotBufs?.get(id);
    if (!p) throw new Error(this.plotBufs ? `unknown plot "${id}"` : "plots are not available in security()");
    return p;
  }

  /** Set an `hline` plot's level (in the plot's units: absolute ticks for price indicators). */
  level(id: string, value: number): void {
    if (!this.levelVals) throw new Error("levels are not available in security()");
    this.levelVals.set(id, value);
  }

  /** Trading day id (CT close date, ADR-0015: the day opens 17:00 CT) of each bar. */
  get tradingDay(): Series {
    if (!this.days) {
      const cache = new TradingDayCache();
      const t = this.bars.time;
      this.days = this.compute((i) => {
        const ts = t.at(i);
        return Number.isNaN(ts) ? Number.NaN : cache.of(ts);
      });
    }
    return this.days;
  }

  /**
   * Session id per bar for an anchor: `eth` = the trading day (17:00 CT open); `rth` = the
   * trading day while inside 08:30–15:00 CT, NaN outside (RTH-anchored tools show nothing there).
   */
  session(anchor: SessionAnchor): Series {
    const have = this.sessions.get(anchor);
    if (have) return have;
    const day = this.tradingDay;
    const t = this.bars.time;
    const s =
      anchor === "eth"
        ? day
        : this.compute((i) => {
            const m = centralMinute(t.at(i));
            return m >= RTH_OPEN_MIN && m < RTH_CLOSE_MIN ? day.at(i) : Number.NaN;
          });
    this.sessions.set(anchor, s);
    return s;
  }

  /**
   * Multi-timeframe (`request.security` with `lookahead_off` and **closed bars only**, SPEC
   * §11A.4 / §12.10.2): aggregates the chart's bars into `timeframe` bars, runs `build` on them
   * and exposes, at each chart bar, the value of the **last completed** higher-timeframe bar —
   * never the forming one, so nothing repaints.
   *
   * Exact when the chart's time bars nest in the requested bars (1m/5m/15m chart → 1h, 4h, 1D …;
   * both are aligned to the 17:00 CT session). On tick/volume/range/Renko charts a bar is
   * attributed by its open time (a warning says so). Only the loaded chart history is
   * aggregated; fetching the higher timeframe from the history loader is the designed follow-up
   * (ADR-0018). "" or the chart's own timeframe runs `build` on the chart itself.
   */
  security(timeframe: string, build: (sub: Context) => Series): Series {
    if (timeframe === "") return build(this);
    const spec = parseTimeframe(timeframe);
    if (!spec || (spec.kind !== "time" && spec.kind !== "session")) {
      this.warnings.push(`timeframe ${timeframe} not supported`);
      return build(this);
    }
    const want = specDurationMs(spec) ?? 0;
    const have = specDurationMs(this.env.spec);
    if (have !== null && want <= have) {
      if (want < have) this.warnings.push(`timeframe ${timeframe} is below the chart's — using the chart`);
      return build(this);
    }
    if (have === null) this.warnings.push(`${timeframe} built from this chart's bars by open time (approx.)`);

    const days = new TradingDayCache();
    // Bucket key of a bar (no per-bar allocation: the open time goes to `keyOpen`).
    let keyOpen = 0;
    const keyOf =
      spec.kind === "time"
        ? (ts: number): number => {
            keyOpen = days.bucketOpen(ts, spec.periodMs);
            return keyOpen;
          }
        : (ts: number): number => {
            const k = sessionKey(spec.unit, spec.count, days.of(ts));
            keyOpen = sessionOpenMs(spec.unit, spec.count, k);
            return k;
          };
    const b = this.bars;
    const key = this.series();
    const j = this.series();
    const rO = this.series();
    const rH = this.series();
    const rL = this.series();
    const rC = this.series();
    const rV = this.series();
    const rBV = this.series();
    const rSV = this.series();
    const rDH = this.series();
    const rDL = this.series();
    const hDH = this.series();
    const hDL = this.series();
    const hO = this.series();
    const hH = this.series();
    const hL = this.series();
    const hC = this.series();
    const hV = this.series();
    const hBV = this.series();
    const hSV = this.series();
    const hT = this.series();
    const sub = new Context(
      {
        open: hO,
        high: hH,
        low: hL,
        close: hC,
        volume: hV,
        buyVolume: hBV,
        sellVolume: hSV,
        deltaHigh: hDH,
        deltaLow: hDL,
        time: hT,
      },
      // Chained sources are chart-indexed: not readable on higher-timeframe bars.
      { ...this.env, spec, resolve: () => null },
      null,
      null,
    );
    const result = build(sub);
    this.growables.push(sub);
    const out = this.series();
    this.nodes.push((i) => {
      const k = keyOf(b.time.at(i));
      const openTs = keyOpen;
      key.data[i] = k;
      const fresh = i === 0 || k !== key.at(i - 1);
      const jj = i === 0 ? 0 : fresh ? j.at(i - 1) + 1 : j.at(i - 1);
      j.data[i] = jj;
      rO.data[i] = fresh ? b.open.at(i) : rO.at(i - 1);
      rH.data[i] = fresh ? b.high.at(i) : Math.max(rH.at(i - 1), b.high.at(i));
      rL.data[i] = fresh ? b.low.at(i) : Math.min(rL.at(i - 1), b.low.at(i));
      rC.data[i] = b.close.at(i);
      // Running-delta extremes across the bucket's bars (bars without them: open 0 and close).
      const d = b.buyVolume.at(i) - b.sellVolume.at(i);
      const dh = Number.isNaN(b.deltaHigh.at(i)) ? Math.max(0, d) : b.deltaHigh.at(i);
      const dl = Number.isNaN(b.deltaLow.at(i)) ? Math.min(0, d) : b.deltaLow.at(i);
      const before = fresh ? 0 : rBV.at(i - 1) - rSV.at(i - 1);
      rDH.data[i] = fresh ? dh : Math.max(rDH.at(i - 1), before + dh);
      rDL.data[i] = fresh ? dl : Math.min(rDL.at(i - 1), before + dl);
      rV.data[i] = (fresh ? 0 : rV.at(i - 1)) + b.volume.at(i);
      rBV.data[i] = (fresh ? 0 : rBV.at(i - 1)) + b.buyVolume.at(i);
      rSV.data[i] = (fresh ? 0 : rSV.at(i - 1)) + b.sellVolume.at(i);
      hO.data[jj] = rO.at(i);
      hH.data[jj] = rH.at(i);
      hL.data[jj] = rL.at(i);
      hC.data[jj] = rC.at(i);
      hV.data[jj] = rV.at(i);
      hBV.data[jj] = rBV.at(i);
      hSV.data[jj] = rSV.at(i);
      hDH.data[jj] = rDH.at(i);
      hDL.data[jj] = rDL.at(i);
      hT.data[jj] = openTs;
      sub.run(jj);
      // Closed higher-timeframe bars only: the one before the forming bucket.
      out.data[i] = jj > 0 ? result.at(jj - 1) : Number.NaN;
    });
    return out;
  }

  /** Grow every series to hold `n` bars. */
  ensure(n: number): void {
    for (const g of this.growables) g.ensure(n);
  }

  /** Run the wired nodes for bar `i`. */
  run(i: number): void {
    const nodes = this.nodes;
    for (let k = 0; k < nodes.length; k++) nodes[k]?.(i);
  }
}
